import React from 'react';
import PropTypes from 'prop-types';
import Button from 'relements/build/esm/ui/button';
import Icon from 'relements/build/esm/ui/icon';
import Accordion from 'relements/build/esm/ui/accordion';
import Text from 'relements/build/esm/input/text';
import Dropdown from 'relements/build/esm/input/dropdown';

import { Portal } from 'react-portal';
import converter from 'number-to-words';

import connection from '@src/connection';
import hashHistory from '@src/History';
import { toTitleCase } from '@src/utils';
import BotStore from '@src/store';

import * as maps from './maps';
import './StepPriorities.scss';

@connection(maps.listenToStores, maps.getStateFromStores)
export default class StepPriorities extends React.Component {
  constructor() {
    super();
    this.renderStepPriority = this.renderStepPriority.bind(this);
    this.state = {
      activeIndex: -1,
    };
  }

  componentDidMount() {
    maps.getStepPriorities(this.props.match.params.botId);
  }

  render() {
    const hasSelfServeLayout = BotStore.getSelfServeLayout();

    return (
      <Portal active>
        <div
          className={`step-priority-overlay ${
            hasSelfServeLayout ? 'step-priority-overlay-ww' : ''
          }`}
        >
          {this.renderHeader(this.props.domain.title)}
          {this.renderTable()}
        </div>
      </Portal>
    );
  }

  renderHeader(title) {
    const hasSelfServeLayout = BotStore.getSelfServeLayout();
    return (
      <div className="step-priority-overlay-header">
        <div className="step-priority-overlay-header-title">
          Step Priorities for 
          {' '}
          <strong>{title}</strong>
        </div>
        <Button type={Button.TYPES.PRIMARY} onClick={this.handleSave}>
          Save
        </Button>
        <Icon
          className="step-priority-overlay-header-icon"
          src="close"
          onClick={() => {
            if (hasSelfServeLayout) {
              this.props.history.goBack();
            } else {
              hashHistory.push(
                this.props.location.prevLocation
                  ? this.props.location.prevLocation
                  : `/bot/${this.props.match.params.botId}`
              );
            }
          }}
        />
      </div>
    );
  }

  renderTable() {
    const { stepPriorities: data } = this.props;
    const { priority_list: priorityList = [] } = data;

    return (
      <div className="step-priority-overlay-body">
        <div className="step-priority-overlay-body-header">
          <div className="step-priority-overlay-body-text">
            <div className="step-priority-overlay-body-title">
              Priority based step transisitions
            </div>
            <div className="step-priority-overlay-body-subtitle">
              Define priorities here to force transitions in case of conflicts.
            </div>
          </div>
          <Button onClick={this.handleAddRow}>Add new Rule</Button>
        </div>
        {priorityList.filter((item) => item.active).length === 0 ? (
          this.renderZeroState()
        ) : (
          <Accordion className="step-priority-overlay-body-card">
            {priorityList.map(this.renderStepPriority(this.getData()))}
          </Accordion>
        )}
      </div>
    );
  }

  renderZeroState = () => {
    return (
      <span style={{ opacity: 0.6, fontSize: 12 }}>
        No active step criterieas
      </span>
    );
  };

  renderStepPriority = (meta) => (priority, i) => {
    if (!priority.active) return null;
    return (
      <Accordion.Item
        className="step-priority-overlay-item"
        itemIndex={i}
        active={this.state.activeIndex === i}
        renderHeader={this.renderStepPriorityHeader(priority, meta)}
        onChange={this.handleToggle}
      >
        {this.renderStepPriorityBody(priority, meta)}
      </Accordion.Item>
    );
  };

  renderStepPriorityHeader = (item, meta) => ({ ExpandIcon }) => {
    return (
      <div className="step-priority-overlay-item-header">
        {item.desc ? (
          <div className="step-priority-overlay-item-header-title">
            {item.desc}
          </div>
        ) : (
          <div className="step-priority-overlay-item-header-title zero-state">
            Enter Priority Criteria
          </div>
        )}
        <div className="step-priority-overlay-item-header-subtitle">
          {item.prioritized_steps.map((step, i) => {
            return (
              <>
                {step.title}
                {item.prioritized_steps.length - 1 !== i ? (
                  <Icon src="angle-down" />
                ) : null}
              </>
            );
          })}
        </div>
        <Icon
          className="step-priority-overlay-item-header-icon"
          src="trash"
          onClick={(e) => this.handleRemoveRow(item.id, e)}
        />
        <ExpandIcon />
      </div>
    );
  };

  renderStepPriorityBody(item, meta) {
    const { maps: map, values, revmap } = meta;
    const stepOptions = values;
    const title = revmap[item.priority.title];
    return (
      <div className="step-priority-overlay-item-body">
        <div className="step-priority-overlay-item-body-form">
          <Text
            className="step-priority-overlay-item-body-step-text"
            placeholder="Criteria"
            label="Enter Criteria"
            value={item.desc}
            onChange={this.handleChange('text', `text-${item.id}`)}
          />
          <Dropdown
            withSearch
            className="step-priority-overlay-item-body-step-dropdown"
            placeholder="Select..."
            label="Prioritized Step"
            options={stepOptions}
            optionKey="title"
            value={{
              title: item.priority.title,
              value: revmap[item.priority.title],
            }}
            onChange={(val) =>
              this.handleChange(
                'inline-select',
                `priority-${item.id}-${
                  item.priority.id
                    ? item.priority.id
                    : Math.floor(Math.random() * 3000)
                }`,
                map
              )(val.title)}
          />
          {item.prioritized_steps.map((step, stepIndex) => {
            if (step.id === item.priority.id) return null;
            return (
              <div className="step-priority-overlay-item-body-step">
                <Dropdown
                  withSearch
                  className="step-priority-overlay-item-body-step-dropdown"
                  label={`${toTitleCase(
                    converter.toWordsOrdinal(stepIndex + 1)
                  )} Step `}
                  options={stepOptions}
                  optionKey="title"
                  value={{
                    title: step.title,
                    value: revmap[step.title],
                  }}
                  onChange={(val) =>
                    this.handleChange(
                      'inline-select',
                      `select-${item.id}-${step.id}`,
                      map
                    )(val.title)}
                />
                <Icon
                  className="trash-icon"
                  src="trash"
                  onClick={() => this.handleRemoveStep(item.id, step.id)}
                />
              </div>
            );
          })}
          <Button onClick={() => this.handleAddStep(item.id)}>
            Add Next Step
          </Button>
        </div>
      </div>
    );
  }

  getData = () => {
    const { stepPriorities: data } = this.props;
    const { stories = [] } = data;
    const defaultSelect = '----';
    const values = [];
    const maps = { id: {}, title: {} };
    const revmap = { '': defaultSelect };

    Object.keys(stories).forEach((storyId) => {
      const storyTitle = stories[storyId].title;
      const subStories = stories[storyId].sub_stories;
      Object.keys(subStories).forEach((substoryId) => {
        const subStoryTitle = subStories[substoryId].title;
        const { nodeList = [] } = subStories[substoryId];
        Object.keys(nodeList).forEach((index) => {
          const stepTitle = nodeList[index].title;
          const stepId = nodeList[index].id;
          const title = `${storyTitle} \u25CF ${subStoryTitle} \u25CF ${stepTitle}`;
          values.push({ title, value: { stepId, substoryId, storyId } });
          maps.id[title] = stepId;
          maps.title[title] = stepTitle;
          revmap[stepTitle] = title;
        });
      });
    });

    return { maps, values, revmap };
  };

  handleChange = (type, id, map) => (value) => {
    maps.updateStepPriorities({
      type,
      value,
      id,
      edited: true,
      map,
    });

    maps.getUpdates();
  };

  handleToggle = (newIndex) => {
    this.setState((state) => ({
      activeIndex: state.activeIndex === newIndex ? -1 : newIndex,
    }));
  };

  handleAddRow = () => {
    maps.updateStepPriorities({
      id: `text-${Math.floor(Math.random() * 30000)}`,
      type: 'text',
      value: '',
      edited: true,
    });

    maps.getUpdates();

    setTimeout(() => {
      this.setState({
        activeIndex: this.props.stepPriorities.priority_list.length - 1,
      });
    }, 0);
  };

  handleRemoveRow(id, e) {
    e.stopPropagation();
    maps.updateStepPriorities(
      {
        id: `trash-${id}`,
      },
      { delete: true }
    );

    maps.getUpdates();
  }

  handleAddStep(id) {
    maps.updateStepPriorities(
      {
        id,
        type: 'update',
      },
      { add: true }
    );

    maps.getUpdates();
  }

  handleRemoveStep(itemId, stepId) {
    maps.updateStepPriorities(
      {
        id: `${itemId}-${stepId}`,
        type: 'update',
      },
      { delete: true }
    );

    maps.getUpdates();
  }

  handleSave = () => {
    maps.sendUpdatedStepPriorities(
      this.props.match.params.botId,
      BotStore.getStepPriorities()
    );
  };
}

StepPriorities.propTypes = {
  botId: PropTypes.string,
  data: PropTypes.object,
  domain: PropTypes.object,
  location: PropTypes.object,
};

StepPriorities.defaultProps = {
  botId: '',
  data: {},
  domain: {},
  location: {},
};
